import { useCallback, useRef, useState, type RefObject } from "react";
import type { AgGridReact } from "@ag-grid-community/react";
import type { FacetTableData } from "./facet-table-helpers";
import {
    cloneFacetTableRows,
    collectFacetTableFromGrid,
    commitFacetTableBatchEdit,
    cancelFacetTableBatchEdit,
    startFacetTableBatchEdit,
} from "./facet-result-table-grid-helpers";

interface UseFacetResultTableBatchEditArgs {
    table: FacetTableData;
    onBatchSave?: (table: FacetTableData) => void | Promise<void>;
    gridRef: RefObject<AgGridReact | null>;
}

export function useFacetResultTableBatchEdit({
    table,
    onBatchSave,
    gridRef,
}: UseFacetResultTableBatchEditArgs) {
    const snapshotRef = useRef<Record<string, unknown>[]>([]);
    const saveViaBatchEventRef = useRef(false);
    const [isEditing, setIsEditing] = useState(false);

    const finishSave = useCallback(
        async (nextTable: FacetTableData) => {
            if (!onBatchSave) return;
            await onBatchSave(nextTable);
            setIsEditing(false);
        },
        [onBatchSave]
    );

    const handleBatchEditingStopped = useCallback(
        async (event: { edits?: unknown[] }) => {
            saveViaBatchEventRef.current = false;
            const api = gridRef.current?.api;
            if (!api) return;
            if (!event.edits?.length) {
                setIsEditing(false);
                return;
            }
            await finishSave(collectFacetTableFromGrid(api, table.headers));
        },
        [finishSave, gridRef, table.headers]
    );

    const handleStartEdit = useCallback(() => {
        const api = gridRef.current?.api;
        if (!api || !onBatchSave) return;
        snapshotRef.current = cloneFacetTableRows(table.rows);
        startFacetTableBatchEdit(api);
        setIsEditing(true);
    }, [gridRef, onBatchSave, table.rows]);

    const handleSave = useCallback(async () => {
        const api = gridRef.current?.api;
        if (!api || !onBatchSave) return;
        const hasBatchCommit = typeof (api as { commitBatchEdit?: () => void }).commitBatchEdit === "function";
        if (hasBatchCommit) {
            saveViaBatchEventRef.current = true;
            commitFacetTableBatchEdit(api);
            queueMicrotask(() => {
                if (saveViaBatchEventRef.current) {
                    saveViaBatchEventRef.current = false;
                    setIsEditing(false);
                }
            });
            return;
        }
        commitFacetTableBatchEdit(api);
        await finishSave(collectFacetTableFromGrid(api, table.headers));
    }, [finishSave, gridRef, onBatchSave, table.headers]);

    const handleCancel = useCallback(() => {
        const api = gridRef.current?.api;
        if (!api) return;
        cancelFacetTableBatchEdit(api, snapshotRef.current);
        setIsEditing(false);
    }, [gridRef]);

    return {
        isEditing,
        handleBatchEditingStopped,
        handleStartEdit,
        handleSave,
        handleCancel,
    };
}
